import type { DataApiModify, Instance, ModifyDataApiAuthProvider } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { differenceInHours } from 'date-fns';
import deepEqual from 'fast-deep-equal';

export const isAuthProviderModified = (
  originalData: DataApiModify | null,
  updated: ModifyDataApiAuthProvider,
): boolean => {
  const original = originalData?.authProviders.find((provider) => provider.id === updated.id);
  if (!original) {
    return false;
  }

  if (original.name !== updated.name) {
    return true;
  }
  if (original.enabled !== updated.enabled) {
    return true;
  }
  if (isNotNullish(original.url) && original.url !== updated.url) {
    return true;
  }
  return false;
};

export const isDataApiModified = (original: DataApiModify | null, updated: DataApiModify): boolean => {
  if (!original) {
    return false;
  }

  if (original.name !== updated.name) {
    return true;
  }
  if (updated.instancePassword) {
    return true;
  }
  if (updated.instanceUsername) {
    return true;
  }
  if (original.typeDefinitions !== updated.typeDefinitions) {
    return true;
  }
  if (original.featuresSubgraph !== updated.featuresSubgraph) {
    return true;
  }
  if (original.securityIntrospection !== updated.securityIntrospection) {
    return true;
  }
  if (original.securityFieldSuggestions !== updated.securityFieldSuggestions) {
    return true;
  }
  if (!deepEqual(original.allowedOrigins, updated.allowedOrigins)) {
    return true;
  }
  return false;
};

export const getRemainingTrialTimeMessage = (instance: Instance): string | null => {
  const trialEndTime = isNotNullish(instance.trialEndTime) ? new Date(instance.trialEndTime) : null;

  const now = new Date();
  if (isNullish(trialEndTime)) {
    return null;
  }

  if (trialEndTime < now) {
    return 'Trial expired';
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call
  const hoursRemaining = Math.max(differenceInHours(trialEndTime, now) + 1, 0);

  if (hoursRemaining < 24) {
    return '1 day trial remaining';
  }

  const daysRemaining = Math.ceil(hoursRemaining / 24);
  return `${daysRemaining} days trial remaining`;
};
